import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  useTheme,
  Alert,
  Stack,
} from "@mui/material";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import api from "../../api/client";

function KindSection({ title, kind, hint, colors }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/design-library", { params: { kind } });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setItems([]);
      const st = e.response?.status;
      const backendMsg = e.response?.data?.message;
      if (st === 404) {
        setMessage(
          "Design library API was not found (404). Stop any old Node process on port 4000, then start the backend again from the project root: npm run server — so the server loads the latest routes."
        );
      } else {
        setMessage(backendMsg || e.message || "Failed to load");
      }
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => {
    load();
  }, [load]);

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setMessage("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      await api.post("/api/design-library", fd);
      await load();
    } catch (err) {
      const st = err.response?.status;
      if (st === 404) {
        setMessage(
          "Upload endpoint not found (404). Restart the backend: npm run server (port 4000), then refresh this page."
        );
      } else {
        setMessage(err.response?.data?.message || err.message || "Upload failed");
      }
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this asset from the library?")) return;
    setMessage("");
    try {
      await api.delete(`/api/design-library/${id}`);
      await load();
    } catch (err) {
      const st = err.response?.status;
      if (st === 404) {
        setMessage("Delete endpoint not found (404). Restart the backend (npm run server), then try again.");
      } else {
        setMessage(err.response?.data?.message || err.message || "Delete failed");
      }
    }
  };

  return (
    <Card sx={{ bgcolor: colors.primary[400], border: `1px solid ${colors.primary[300]}`, mb: 3 }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {hint}
            </Typography>
          </Box>
          <Button variant="contained" color="secondary" component="label" disabled={uploading}>
            {uploading ? "Uploading…" : "Upload"}
            <input type="file" hidden accept=".png,.jpg,.jpeg,.gif,.webp,.svg,image/*" onChange={onFile} />
          </Button>
        </Stack>
        {message ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {message}
          </Alert>
        ) : null}
        {loading ? (
          <Typography variant="body2">Loading…</Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
              gap: 2,
            }}
          >
            {items.map((it) => (
              <Box
                key={it.id}
                sx={{
                  position: "relative",
                  borderRadius: 1,
                  overflow: "hidden",
                  border: `1px solid ${colors.primary[300]}`,
                  bgcolor: colors.primary[500],
                }}
              >
                <Box
                  component="img"
                  src={it.url}
                  alt={it.originalName || it.kind}
                  sx={{ width: "100%", height: 140, objectFit: "cover", display: "block" }}
                />
                <Box sx={{ p: 1 }}>
                  <Typography variant="caption" noWrap title={it.originalName}>
                    {it.originalName || "Untitled"}
                  </Typography>
                  <Button size="small" color="error" variant="text" onClick={() => remove(it.id)}>
                    Remove
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default function DesignLibrary() {
  const colors = tokens(useTheme().palette.mode);
  const [legacyApi, setLegacyApi] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .get("/api/health")
      .then(({ data }) => {
        if (!alive || !data || !data.ok) return;
        if (!data.features || !data.features.designLibrary) setLegacyApi(true);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  return (
    <Box m="20px">
      <Header
        title="BACKGROUNDS & VECTORS"
        subtitle="Design elements for the layout editor. Designers pick these from the Backgrounds and Vectors side panels while editing."
      />
      {legacyApi ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This API build does not advertise the design library. Stop the old server on port 4000 and run{" "}
          <strong>npm run server</strong> from the project root so <code>/api/design-library</code> is available.
        </Alert>
      ) : null}
      <KindSection
        title="Background images"
        kind="background"
        hint="Raster backgrounds (PNG, JPEG, GIF, WebP)."
        colors={colors}
      />
      <KindSection
        title="Vectors"
        kind="vector"
        hint="SVG artwork — inserted like images on the canvas."
        colors={colors}
      />
    </Box>
  );
}
